import apiService from '../api/api.service';
import { PaginatedResponse } from '../api/api.service';
import { DepartmentDto, UpdateDepartmentDto, LeaveApprovalHierarchy, HierarchyStepPayload } from '../../types/department.types';
import { store } from '../../store/store';

class DepartmentService {
  private static instance: DepartmentService;

  private constructor() {}

  public static getInstance(): DepartmentService {
    if (!DepartmentService.instance) {
      DepartmentService.instance = new DepartmentService();
    }
    return DepartmentService.instance;
  }

  public async getDepartments(): Promise<DepartmentDto[]> {
    const response = await apiService.get<DepartmentDto[]>("/departments");
    return response;
  }

  public async getAllDepartments(companyId: number): Promise<PaginatedResponse<DepartmentDto>> {
    return apiService.get(`/departments/company/${companyId}`);
  }

  public async getDepartmentById(id: number): Promise<DepartmentDto> {
    return apiService.get(`/departments/${id}`);
  }

  public async createDepartment(data: DepartmentDto): Promise<DepartmentDto> {
    return apiService.post('/departments', data);
  }

  public async updateDepartment(id: number, data: DepartmentDto): Promise<DepartmentDto> {
    return apiService.put(`/departments/${id}`, data);
  }

  public async updateDepartmentBySuperAdmin(id: number, data: Partial<UpdateDepartmentDto>): Promise<DepartmentDto> {
    return apiService.patch(`/super-admin/department/${id}`, data);
  }

  public async deleteDepartment(id: number): Promise<void> {
    return apiService.delete(`/departments/${id}`);
  }

  public async getDepartmentByBranchId(
    branchId: number
  ): Promise<DepartmentDto[]> {
    const response = await apiService.get<DepartmentDto[]>(
      `/departments/branch/${branchId}`
    );
    return response;
  }

  /**
   * Fetches the leave approval hierarchy for a specific department.
   * @param departmentId The ID of the department.
   * @returns The leave approval hierarchy, or null if it doesn't exist.
   */
  public async getDepartmentHierarchy(departmentId: number): Promise<LeaveApprovalHierarchy | null> {
    const response = await apiService.get<LeaveApprovalHierarchy | null>(`/departments/${departmentId}/hierarchy`);
    return response;
  }

  /**
   * Creates or updates the leave approval hierarchy for a department.
   * @param departmentId The ID of the department.
   * @param steps An array of hierarchy steps.
   * @returns The updated leave approval hierarchy.
   */
  public async createOrUpdateDepartmentHierarchy(departmentId: number, steps: HierarchyStepPayload[]): Promise<LeaveApprovalHierarchy> {
    const response = await apiService.post<LeaveApprovalHierarchy>(`/departments/${departmentId}/hierarchy`, { steps });
    return response;
  }

  /**
   * Deletes the leave approval hierarchy for a department.
   * @param departmentId The ID of the department.
   */
  public async deleteDepartmentHierarchy(departmentId: number): Promise<void> {
    await apiService.delete(`/departments/${departmentId}/hierarchy`);
  }
}

export default DepartmentService.getInstance();
